(function (globalScope, factory) {
  const api = factory(globalScope);
  if (typeof module === "object" && module.exports) module.exports = api;
  if (globalScope) globalScope.HomeWellnessResults = api;
})(typeof window !== "undefined" ? window : globalThis, function (globalScope) {
  "use strict";

  // Brand icon set for the dynamically rendered parts of the results. These
  // are the same marks as snippets/human-standard-brand-icon.liquid; keep the
  // two in sync so a priority card matches the static header treatment.
  const ICON_ATTRS = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"';

  const ICONS = Object.freeze({
    guidanceBadge: `<svg class="hs-brand-icon hs-brand-icon--guidance-badge" width="15" height="15" ${ICON_ATTRS}><path d="M12 2.9l2.3 1.5 2.7.2 1 2.6 2 1.9-.9 2.6.4 2.7-2.4 1.3-1.5 2.3-2.7-.3L12 19.4l-2.4-1.7-2.7.3-1.5-2.3-2.4-1.3.4-2.7-.9-2.6 2-1.9 1-2.6 2.7-.2Z"></path><path d="M8.9 11.4l2.2 2.2 4-4.2"></path></svg>`,
    research: `<svg class="hs-brand-icon hs-brand-icon--research" width="15" height="15" ${ICON_ATTRS}><path d="M6.4 3.6h8l3.9 3.9v12.9H6.4Z"></path><path d="M14.1 3.6v4h4.2"></path><path d="M9.2 12.1h5.6"></path><path d="M9.2 15.6h4"></path></svg>`,
    resource: `<svg class="hs-brand-icon hs-brand-icon--resource" width="15" height="15" ${ICON_ATTRS}><path d="M13.6 4.4h6v6"></path><path d="M19.6 4.4 11 13"></path><path d="M17.2 14.3v4.4a1.7 1.7 0 0 1-1.7 1.7H5.7A1.7 1.7 0 0 1 4 18.7V8.9a1.7 1.7 0 0 1 1.7-1.7h4.4"></path></svg>`
  });

  const CATEGORY_LABELS = Object.freeze({
    maintenance: "Keep building",
    kitchenPlastics: "Food + beverage",
    nonstick: "Cookware",
    fragrance: "Fragrance",
    cleaningLaundry: "Cleaning + laundry",
    pfasFabrics: "Fabrics + finishes",
    dust: "Shoes + dust",
    indoorAir: "Indoor air",
    water: "Drinking water",
    mold: "Moisture + mold",
    pesticides: "Outdoor treatments",
    kidsPlastics: "Kids food-contact",
    kidsToys: "Kids toys",
    kidsTextiles: "Kids textiles",
    textiles: "Textiles"
  });

  // Every finite recommendation key maps to one approved, decorative asset.
  const CATEGORY_ICON_MAP = Object.freeze({
    maintenance: "maintenance",
    kitchenPlastics: "kitchenPlastics",
    nonstick: "nonstick",
    fragrance: "fragrance",
    cleaningLaundry: "cleaningLaundry",
    pfasFabrics: "pfasFabrics",
    dust: "dust",
    indoorAir: "indoorAir",
    water: "water",
    mold: "mold",
    pesticides: "pesticides",
    kidsPlastics: "kidsPlastics",
    kidsToys: "kidsToys",
    kidsTextiles: "kidsTextiles",
    textiles: "textiles"
  });

  const FALLBACK_ICON = "maintenance";
  const VEHICLE_GARAGE_SAFETY_NOTICE = "Never run or warm a vehicle in an attached garage, even with the door open. Move it outside immediately and make sure working carbon monoxide alarms are installed near sleeping areas.";
  const FUEL_BURNING_SAFETY_NOTICE = "Fuel-burning equipment belongs outdoors and away from doors, windows, and vents. If you suspect carbon monoxide exposure or anyone feels suddenly ill, move to fresh air and contact emergency services or Poison Control.";
  const PERSONALIZED_SAFETY_ALERTS = Object.freeze([VEHICLE_GARAGE_SAFETY_NOTICE, FUEL_BURNING_SAFETY_NOTICE]);
  const APPROVED_INTERNAL_ROUTES = Object.freeze(new Set([
    "/pages/home-wellness-baseline",
    "/pages/home-wellness-baseline-results",
    "/policies/privacy-policy",
    "/policies/terms-of-service"
  ]));

  function text(value) {
    return String(value == null ? "" : value).trim();
  }

  function escapeHTML(value) {
    return text(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function safeArray(value) {
    return Array.isArray(value) ? value : [];
  }

  function safeUrl(value) {
    const candidate = text(value);
    if (!candidate) return "";
    if (candidate.startsWith("/")) return candidate;
    try {
      const parsed = new URL(candidate);
      return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : "";
    } catch (_error) {
      return "";
    }
  }

  function safeInternalUrl(value) {
    const url = safeUrl(value);
    if (!url || !url.startsWith("/")) return "";
    const pathname = url.split(/[?#]/, 1)[0];
    return APPROVED_INTERNAL_ROUTES.has(pathname) || pathname.startsWith("/blogs/learn/") ? url : "";
  }

  function safeExternalUrl(value) {
    const url = safeUrl(value);
    if (!url || url.startsWith("/")) return "";
    try {
      const parsed = new URL(url);
      return parsed.hostname && !/^(example\.com|localhost)$/i.test(parsed.hostname) ? parsed.href : "";
    } catch (_error) {
      return "";
    }
  }

  function clampScore(value) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? Math.max(0, Math.min(100, Math.round(numeric))) : null;
  }

  function categoryLabel(key, fallback) {
    return text(fallback) || CATEGORY_LABELS[key] || "Home wellness priority";
  }

  // Mirrors the Baseline Arch path in snippets/home-wellness-results.liquid:
  // a half circle of radius 80 centred on (100, 112), swept left to right.
  const ARCH = Object.freeze({ centerX: 100, centerY: 112, radius: 80 });

  function archGeometry(score) {
    const value = score == null ? 0 : score;
    const angle = Math.PI * (1 - value / 100);
    return {
      dashOffset: 100 - value,
      locatorX: ARCH.centerX + ARCH.radius * Math.cos(angle),
      locatorY: ARCH.centerY - ARCH.radius * Math.sin(angle)
    };
  }

  function prefersReducedMotion() {
    return typeof globalScope.matchMedia === "function"
      && globalScope.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  // The arch entrance is a one-time reveal. It waits until the results screen
  // is active and a meaningful share of the score sits in the central viewport
  // band. Once committed it is never reset, so scrolling away cannot replay it.
  const ARCH_REVEAL = Object.freeze({
    minRatio: 0.6,
    threshold: Object.freeze([0, 0.25, 0.55, 0.6, 0.65, 1]),
    rootMargin: "-20% 0px -20% 0px"
  });
  const archRevealCleanups = typeof WeakMap === "function" ? new WeakMap() : null;

  function isResultsScreenActive(el) {
    const screen = el && typeof el.closest === "function" ? el.closest('[data-screen="results"]') : null;
    if (!screen) return true;
    return !!(screen.classList && screen.classList.contains("is-active"));
  }

  function shouldRevealArch(entry) {
    if (!entry || !entry.isIntersecting) return false;
    const ratio = Number(entry.intersectionRatio);
    return Number.isFinite(ratio) && ratio >= ARCH_REVEAL.minRatio;
  }

  function revealArch(arch) {
    if (!arch || arch.dataset.archRevealed === "true") return;
    if (archRevealCleanups && archRevealCleanups.has(arch)) {
      const previous = archRevealCleanups.get(arch);
      archRevealCleanups.delete(arch);
      if (typeof previous === "function") previous();
    }

    let observer = null;
    let mutation = null;
    const teardown = () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (mutation) {
        mutation.disconnect();
        mutation = null;
      }
    };
    const commit = (instant) => {
      if (arch.dataset.archRevealed === "true") return;
      teardown();
      if (archRevealCleanups) archRevealCleanups.delete(arch);
      arch.dataset.archRevealed = "true";
      if (instant) arch.classList.add("is-instant");
      arch.classList.add("is-revealed");
    };

    if (prefersReducedMotion() || typeof globalScope.IntersectionObserver !== "function") {
      commit(true);
      return;
    }

    const startObserver = () => {
      if (observer || arch.dataset.archRevealed === "true") return;
      observer = new globalScope.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!isResultsScreenActive(arch)) return;
          if (!shouldRevealArch(entry)) return;
          commit(false);
        });
      }, { threshold: ARCH_REVEAL.threshold.slice(), rootMargin: ARCH_REVEAL.rootMargin });
      observer.observe(arch);
    };

    if (archRevealCleanups) archRevealCleanups.set(arch, teardown);

    if (!isResultsScreenActive(arch)) {
      const screen = arch.closest('[data-screen="results"]');
      if (screen && typeof globalScope.MutationObserver === "function") {
        mutation = new globalScope.MutationObserver(() => {
          if (isResultsScreenActive(arch)) startObserver();
        });
        mutation.observe(screen, { attributes: true, attributeFilter: ["class"] });
        return;
      }
    }
    startObserver();
  }

  // The celebration sparkle follows the same one-time contract as the arch:
  // it plays when the panel first enters view and is never replayed.
  function revealSparkle(sparkle) {
    if (!sparkle || sparkle.dataset.sparkleRevealed === "true") return;
    const commit = () => {
      sparkle.dataset.sparkleRevealed = "true";
      sparkle.classList.add("is-revealed");
    };
    if (prefersReducedMotion() || typeof globalScope.IntersectionObserver !== "function") {
      sparkle.classList.add("is-instant");
      commit();
      return;
    }
    const observer = new globalScope.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        commit();
      });
    }, { threshold: 0.35 });
    observer.observe(sparkle);
  }

  function iconKey(category) {
    return CATEGORY_ICON_MAP[category] || FALLBACK_ICON;
  }

  function iconAsset(category) {
    const assets = globalScope && globalScope.HumanStandardResultIconAssets;
    return assets && typeof assets === "object" ? text(assets[iconKey(category)]) : "";
  }

  function learnUrl(value) {
    const candidate = text(value);
    if (!candidate) return "";
    if (candidate.startsWith("/")) return safeInternalUrl(candidate);
    try {
      const parsed = new URL(candidate);
      if (!["http:", "https:"].includes(parsed.protocol)) return "";
      const host = parsed.hostname.replace(/^www\./i, "");
      if (host === "humanstandardliving.com" && parsed.pathname.startsWith("/blogs/learn/")) {
        return parsed.pathname + parsed.search + parsed.hash;
      }
      return "";
    } catch (_error) {
      return "";
    }
  }

  function normalizeLink(link, type) {
    const url = type === "research" ? safeExternalUrl(link && link.url) : (type === "learn" ? learnUrl(link && link.url) : safeInternalUrl(link && link.url));
    const label = text(link && link.label);
    return url && label ? { label, url, marker: text(link.marker), disclosure: text(link.disclosure) } : null;
  }

  function normalizeRecommendation(rec, index, categories) {
    const source = rec || {};
    const key = text(source.key) || text(categories[index]) || "maintenance";
    const whyParagraphs = safeArray(source.why_paragraphs).map(text).filter(Boolean);
    const why = text(source.why) || whyParagraphs[0] || "This is a practical place to begin based on the routines and products reflected in your Baseline.";
    const action = text(source.first_action || source.action) || "Choose one small, repeatable change that fits your household this week.";
    return {
      key,
      category: categoryLabel(key, source.tag),
      icon: iconKey(key),
      title: text(source.title) || "A practical next step for your home",
      why,
      whyParagraphs: whyParagraphs.length ? whyParagraphs : [why],
      action,
      evidenceLabel: text(source.evidence_label),
      evidenceNote: text(source.evidence_note),
      learnMore: safeArray(source.learn_more).map(link => normalizeLink(link, "learn")).filter(Boolean),
      products: safeArray(source.helpful_products).map(link => normalizeLink(link, "product")).filter(Boolean),
      affiliateDisclosure: text(source.affiliate_disclosure),
      sources: safeArray(source.research_sources).map(link => normalizeLink(link, "research")).filter(Boolean)
    };
  }

  function normalize(input) {
    const source = input || {};
    const score = clampScore(source.score);
    const categories = safeArray(source.top_categories || source.topCategories).map(text).filter(Boolean).slice(0, 3);
    const recSource = safeArray(source.top_recommendations || source.topRecommendations);
    const recommendations = recSource.slice(0, 3).map((rec, index) => normalizeRecommendation(rec, index, categories));
    const firstName = text(source.first_name || source.firstName).split(/\s+/)[0];
    const fallbackGood = "You have already taken a thoughtful first step by completing your Baseline.";
    const fallbackCalm = "Nothing outside these priorities needs to become an urgent whole-home project.";
    const notices = safeArray(source.safety_notices || source.safetyNotices).map(text).filter(Boolean);
    const safetyAlerts = PERSONALIZED_SAFETY_ALERTS.filter((alert) => notices.includes(alert));
    const safety = notices.filter((item) => PERSONALIZED_SAFETY_ALERTS.indexOf(item) === -1).slice(0, 3);
    return {
      contentVersion: text(source.content_version || source.contentVersion) || "home_wellness_baseline_results_shared_v1",
      resultType: text(source.result_type || source.resultType) || "home_wellness_baseline",
      firstName,
      score,
      scoreBand: text(source.score_band_label || source.scoreBandLabel) || "Your starting point",
      scoreNote: text(source.score_interpretation || source.scoreNote) || "Your Score is a progress measure, not a grade. It can change as products, routines, and healthier defaults change.",
      categories,
      recommendations,
      doingGreat: (safeArray(source.doing_great_notes || source.doingGreatNotes).map(text).filter(Boolean).slice(0, 4).length ? safeArray(source.doing_great_notes || source.doingGreatNotes).map(text).filter(Boolean).slice(0, 4) : [fallbackGood]),
      notYet: (safeArray(source.not_to_worry_notes || source.notToWorryNotes).map(text).filter(Boolean).slice(0, 4).length ? safeArray(source.not_to_worry_notes || source.notToWorryNotes).map(text).filter(Boolean).slice(0, 4) : [fallbackCalm]),
      safety,
      safetyAlerts
    };
  }

  function iconSlot(rec) {
    const source = iconAsset(rec.key);
    return source ? `<span class="hwr__icon-slot" data-hwr-icon-key="${escapeHTML(rec.icon)}" aria-hidden="true"><img src="${escapeHTML(source)}" alt="" width="72" height="72" loading="lazy" decoding="async"></span>` : "";
  }

  function researchHTML(rec, index) {
    const extendedWhy = rec.whyParagraphs.filter((paragraph, paragraphIndex) => paragraphIndex > 0 || paragraph !== rec.why);
    const productDisclosure = rec.products.length && rec.affiliateDisclosure ? `<p>${escapeHTML(rec.affiliateDisclosure)}</p>` : "";
    const resources = [extendedWhy.length, rec.learnMore.length, rec.sources.length, rec.products.length, Boolean(productDisclosure)].some(Boolean);
    if (!resources) return "";
    return `<details class="hwr__research-item">
      <summary>Tell me more</summary>
      <div class="hwr__research-body">
        ${extendedWhy.map(paragraph => `<p>${escapeHTML(paragraph)}</p>`).join("")}
        ${linkList("Learn more", rec.learnMore)}
        ${linkList("Supporting research", rec.sources)}
        ${linkList("Optional product guidance", rec.products, "products")}
        ${productDisclosure}
      </div>
    </details>`;
  }

  function priorityHTML(rec, index) {
    return `<article class="hwr__priority" data-hwr-category="${escapeHTML(rec.key)}">
      <div>${iconSlot(rec)}<span class="hwr__priority-number">0${index + 1}</span></div>
      <div>
        <span class="hwr__priority-category">${escapeHTML(rec.category)}</span>
        <h3>${escapeHTML(rec.title)}</h3>
        <p class="hwr__priority-why"><strong>Why this rose to the top:</strong> ${escapeHTML(rec.why)}</p>
        <p class="hwr__first-action"><strong>First action</strong>${escapeHTML(rec.action)}</p>
        ${rec.evidenceNote ? `<p class="hwr__evidence">${rec.evidenceLabel ? `<span class="hwr__evidence-label"><span class="hwr__evidence-mark" aria-hidden="true">${ICONS.guidanceBadge}</span>${escapeHTML(rec.evidenceLabel)}</span>` : ""}${escapeHTML(rec.evidenceNote)}</p>` : ""}
        ${researchHTML(rec, index)}
      </div>
    </article>`;
  }

  function linkList(heading, links, variant) {
    if (!links.length) return "";
    const modifier = variant ? ` hwr__resource-group--${variant}` : "";
    // Product guidance points off-site; everything else is reference material.
    const icon = variant === "products" ? ICONS.resource : ICONS.research;
    const items = links.map(link => `<li><a href="${escapeHTML(link.url)}" target="_blank" rel="noopener noreferrer"><span class="hwr__resource-mark" aria-hidden="true">${icon}</span><span>${escapeHTML(link.marker ? `${link.marker} ${link.label}` : link.label)}</span></a></li>`).join("");
    return `<div class="hwr__resource-group${modifier}"><strong>${escapeHTML(heading)}</strong><ul>${items}</ul></div>`;
  }

  // Supportive-context notes are authored as "Subject: sentence". Emphasising
  // the subject is presentation only; the note text itself is never altered.
  const NOTE_SUBJECT = /^([A-Za-z][A-Za-z0-9 +&'’-]{1,25}):\s+(.+)$/;

  function noteHTML(item) {
    const parts = NOTE_SUBJECT.exec(item);
    if (!parts) return `<li>${escapeHTML(item)}</li>`;
    return `<li><strong class="hwr__note-subject">${escapeHTML(parts[1])}:</strong> ${escapeHTML(parts[2])}</li>`;
  }

  function renderLists(items) {
    return items.map(noteHTML).join("");
  }

  function safetyNoteHTML(alerts) {
    if (!alerts.length) return "";
    const items = alerts.map((alert) => `<p class="hwr__safety-note-item">${escapeHTML(alert)}</p>`).join("");
    return `<aside class="hwr__safety-note" data-hwr-safety-note>
      <p class="hwr__priority-category">Garage + Outdoors</p>
      <h3>An important safety note based on your answers</h3>
      ${items}
    </aside>`;
  }

  function prioritiesHeading(count) {
    const n = Number(count);
    if (n === 3) return "Your three highest priorities";
    if (n === 2) return "Your clearest places to begin";
    return "Your clearest place to begin";
  }

  function renderToString(input) {
    const model = normalize(input);
    return {
      model,
      title: model.firstName ? `${model.firstName}, your personalized Baseline is ready.` : "Your personalized Baseline is ready.",
      intro: "This is a starting point that can evolve with your products, routines, and home. It is not a judgment or a demand for perfection.",
      heading: prioritiesHeading(model.recommendations.length),
      priorities: model.recommendations.map(priorityHTML).join("") + safetyNoteHTML(model.safetyAlerts),
      good: renderLists(model.doingGreat),
      calm: renderLists(model.notYet),
      safety: model.safety.map(item => `<p>${escapeHTML(item)}</p>`).join("")
    };
  }

  function render(root, input) {
    if (!root) throw new Error("A canonical results root is required.");
    const output = renderToString(input);
    const model = output.model;
    const score = model.score;
    const scoreRing = root.querySelector("[data-hwr-score-ring]");
    const scoreLocator = root.querySelector("[data-hwr-score-locator]");
    const scoreValue = root.querySelector("[data-hwr-score]");
    const safety = root.querySelector("[data-hwr-safety]");
    const geometry = archGeometry(score);

    root.querySelector("[data-hwr-title]").textContent = output.title;
    root.querySelector("[data-hwr-intro]").textContent = output.intro;
    scoreValue.textContent = score == null ? "--" : String(score);
    scoreRing.style.setProperty("--hwr-arch-offset", String(geometry.dashOffset));
    if (scoreLocator) {
      scoreLocator.setAttribute("cx", geometry.locatorX.toFixed(2));
      scoreLocator.setAttribute("cy", geometry.locatorY.toFixed(2));
      // With no score there is nothing to locate, so the marker stays absent.
      scoreLocator.style.display = score == null ? "none" : "";
    }
    scoreRing.setAttribute("aria-label", score == null ? "Home Wellness Score unavailable" : `Home Wellness Score: ${score} out of 100`);
    if (score != null) revealArch(scoreRing);
    root.querySelector("[data-hwr-score-band]").textContent = model.scoreBand;
    root.querySelector("[data-hwr-score-note]").textContent = model.scoreNote;
    root.querySelector("[data-hwr-priorities]").innerHTML = output.priorities;
    const prioritiesTitle = root.querySelector("[data-hwr-priorities-title]");
    if (prioritiesTitle) prioritiesTitle.textContent = output.heading;
    root.querySelector("[data-hwr-good]").innerHTML = output.good;
    revealSparkle(root.querySelector(".hwr__sparkle"));
    root.querySelector("[data-hwr-calm]").innerHTML = output.calm;
    safety.hidden = !model.safety.length;
    safety.innerHTML = output.safety;
    root.hidden = false;
    root.dataset.resultsRendered = "true";
    return model;
  }

  return Object.freeze({
    ARCH,
    ARCH_REVEAL,
    CATEGORY_LABELS,
    CATEGORY_ICON_MAP,
    FALLBACK_ICON,
    VEHICLE_GARAGE_SAFETY_NOTICE,
    FUEL_BURNING_SAFETY_NOTICE,
    archGeometry,
    categoryLabel,
    iconAsset,
    iconKey,
    isResultsScreenActive,
    shouldRevealArch,
    revealArch,
    normalize,
    prioritiesHeading,
    render,
    renderToString
  });
});
